import {
  Check,
  CheckCircle,
  Plus,
  Trash,
  Warning,
  XCircle,
} from "@phosphor-icons/react"
import {
  Button,
  Flex,
  FormInput,
  FormQuerySelector,
  FormSelector,
  NoDataFound,
  Note,
  Selector,
  Tabs,
  Tag,
  Tooltip,
} from "@vesatogo/grass-core"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import { useState } from "react"
import { useClient } from "urql"
import { FormCard } from "~/components/FormCard"
import { GstAttachment, GstBreakdownCard } from "~/components/GstBreakdownCard"
import Codenames from "~/constants/Codenames"
import { ADVANCE_KINDS, EXPENSE_KINDS } from "~/constants/static.items"
import {
  ExpenseConfigsQuery,
  ExpenseConfigsQueryVariables,
  useAdancedPaymentParametersQuery,
  useExpenseConfigVersionsQuery,
  useExpenseParametersQuery,
} from "~/generated/graphql"
import { EXPENSE_CONFIGS } from "~/graphql/trade.gql"

const CHARGE_FIELDS = [
  { key: "seller", label: "Supplier Charge" },
  { key: "buyer", label: "Buyer Charge" },
  { key: "facilitator", label: "Facilitator Charge" },
] as const

export type Expense = {
  expense_parameter: any
  seller: {
    id?: any
    paid_by: "seller"
    expense_parameter: any
    kind: any
    value: any
    amount: any
  } | null
  buyer: {
    id?: any
    paid_by: "buyer"
    expense_parameter: any
    kind: any
    value: any
    amount: any
  } | null
  facilitator: {
    paid_by: "facilitator"
    expense_parameter: any
    kind: any
    value: any
    amount: any
  } | null
  kind: any
  amount: any
  // Per-expense GST fields
  supplier_gst_auto_calculated?: boolean
  supplier_igst?: number
  supplier_cgst?: number
  supplier_sgst?: number
  supplier_igst_percentage?: number
  supplier_cgst_percentage?: number
  supplier_sgst_percentage?: number
  seller_gst_attachments?: GstAttachment[]
  buyer_gst_auto_calculated?: boolean
  buyer_igst?: number
  buyer_cgst?: number
  buyer_sgst?: number
  buyer_igst_percentage?: number
  buyer_cgst_percentage?: number
  buyer_sgst_percentage?: number
  buyer_gst_attachments?: GstAttachment[]
}

export type Advaced_Payment = {
  parameter: any
  bourn_by: string
  amount: number
}

export const EXPENSE: Expense = {
  expense_parameter: null,
  kind: null,
  amount: 0,
  seller: {
    paid_by: "seller",
    value: null,
    amount: null,
    kind: null,
    expense_parameter: null,
  },
  buyer: {
    paid_by: "buyer",
    value: null,
    amount: null,
    kind: null,
    expense_parameter: null,
  },
  facilitator: {
    paid_by: "facilitator",
    value: null,
    amount: null,
    kind: null,
    expense_parameter: null,
  },
  supplier_gst_auto_calculated: false,
  supplier_igst: 0,
  supplier_cgst: 0,
  supplier_sgst: 0,
  supplier_igst_percentage: 0,
  supplier_cgst_percentage: 0,
  supplier_sgst_percentage: 0,
  seller_gst_attachments: [],
  buyer_gst_auto_calculated: false,
  buyer_igst: 0,
  buyer_cgst: 0,
  buyer_sgst: 0,
  buyer_igst_percentage: 0,
  buyer_cgst_percentage: 0,
  buyer_sgst_percentage: 0,
  buyer_gst_attachments: [],
}

export const ADVANCED_PAYMENT: Advaced_Payment = {
  parameter: null,
  bourn_by: "",
  amount: 0,
}

export type TradeExpensesProps = {
  isDisabled?: boolean
  form: any
  sharedDeptId: string
  inputProps: any
  onExpenseTemplateChange?: (expenseConfig: ExpenseConfigsQuery) => void
  onDeleteAdvancePayment?: (idx: number, advancePayment: any) => void
  onDeleteExpense?: (idx: number, expense: any) => void
  showOverallExpenseGst?: boolean
  supplierSameState?: boolean
  buyerSameState?: boolean
  expenseGstManualOnly?: boolean
  onExpenseGstFieldChange?: (field: string, value: any) => void
  onRemoveExpenseSellerGstAttachment?: (index: number) => void
  onRemoveExpenseBuyerGstAttachment?: (index: number) => void
  isStatusGenerateSale?: boolean
  hasInvoice?: boolean
  advancePaymentDisabled?: boolean
  creditNoteDisabled?: boolean
  isReceipt?: boolean
}

const TradeExpenses = ({
  form,
  sharedDeptId,
  isDisabled,
  inputProps,
  onExpenseTemplateChange,
  onDeleteExpense,
  onDeleteAdvancePayment,
  showOverallExpenseGst = false,
  supplierSameState,
  buyerSameState,
  expenseGstManualOnly = false,
  onExpenseGstFieldChange,
  onRemoveExpenseSellerGstAttachment,
  onRemoveExpenseBuyerGstAttachment,
  isStatusGenerateSale = false,
  hasInvoice = false,
  advancePaymentDisabled = false,
  creditNoteDisabled = false,
  isReceipt = false,
}: TradeExpensesProps) => {
  const setExpenseGstField = (field: string, value: any) => {
    form.setFieldValue(field, value)
    onExpenseGstFieldChange?.(field, value)
  }
  const expenseSupplierSgst = form.values.expense_supplier_sgst || 0
  const expenseSupplierCgst = form.values.expense_supplier_cgst || 0
  const expenseSupplierIgst = form.values.expense_supplier_igst || 0
  const expenseBuyerSgst = form.values.expense_buyer_sgst || 0
  const expenseBuyerCgst = form.values.expense_buyer_cgst || 0
  const expenseBuyerIgst = form.values.expense_buyer_igst || 0
  const expenseSupplierSgstPercentage =
    form.values.expense_supplier_sgst_percentage
  const expenseSupplierCgstPercentage =
    form.values.expense_supplier_cgst_percentage
  const expenseSupplierIgstPercentage =
    form.values.expense_supplier_igst_percentage
  const expenseBuyerSgstPercentage = form.values.expense_buyer_sgst_percentage
  const expenseBuyerCgstPercentage = form.values.expense_buyer_cgst_percentage
  const expenseBuyerIgstPercentage = form.values.expense_buyer_igst_percentage
  const expenseSupplierGstAutoCalculated =
    form.values.expense_supplier_gst_auto_calculated ?? true
  const expenseBuyerGstAutoCalculated =
    form.values.expense_buyer_gst_auto_calculated ?? true
  const expenseSellerGstAttachments =
    form.values.expense_seller_gst_attachments || []
  const expenseBuyerGstAttachments =
    form.values.expense_buyer_gst_attachments || []
  const [expenseTemplate, setExpenseTemplate] = useState<any>()
  const [activeTab, setActiveTab] = useState("tradeExpenses")
  const client = useClient()
  const totalSupplierGst =
    expenseSupplierIgst + expenseSupplierSgst + expenseSupplierCgst
  const totalBuyerGst = expenseBuyerIgst + expenseBuyerSgst + expenseBuyerCgst
  const [{ data: expenseConfigList }] = useExpenseConfigVersionsQuery({
    variables: {
      department: `${sharedDeptId}`,
      kind: form.values.kind,
      mode: form.values.mode,
    },
    pause: !sharedDeptId || !form.values.mode || !form.values.kind,
    requestPolicy: "network-only",
  })

  const isVerified = form?.values?.credit_notes?.[0]?.is_verified

  return (
    <>
      <FormCard title="Finance">
        <Tabs
          tabs={[
            {
              id: "tradeExpenses",
              header: <h6 className="text-base font-500">Trade Expenses</h6>,
              panel: (
                <>
                  {form.values?.trades?.length > 0 &&
                    form.values?.__typename !== "trade" && (
                      <Note
                        className="mb-3"
                        intent="info"
                        title="All expenses are individually applied to every trade"
                      />
                    )}
                  {form.values?.expenses?.length ? (
                    <div className="-mx-3 md:mx-0">
                      <div className="px-3 md:px-0">
                        <table className="expenses-table w-full block md:table">
                          <thead className="bg-green-100 text-sm rounded hidden md:table-header-group">
                            <tr>
                              <th className="p-1 w-[20%]">Expense Parameter</th>
                              <th className="p-1 w-[20%]">Expense Type</th>
                              <th className="p-1 w-[15%]">Supplier Charge</th>
                              <th className="p-1 w-[15%]">Buyer Charge</th>
                              <th className="p-1 w-[15%]">
                                Facilitator Charge
                              </th>
                              <th className="p-1 text-right w-[10%]">Amount</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody className="block md:table-row-group">
                            {form.values.expenses?.map((expense, idx) => {
                              const isExpenseDisabled =
                                isStatusGenerateSale && hasInvoice
                                  ? true
                                  : isStatusGenerateSale &&
                                    !hasInvoice &&
                                    expense?.expense_parameter?.codename !==
                                      Codenames.PackagingExpense &&
                                    expense?.expense_parameter?.codename !==
                                      undefined
                                  ? true
                                  : isStatusGenerateSale && !hasInvoice
                                  ? false
                                  : isDisabled

                              const rightElement = expense?.kind?.short && (
                                <Tag
                                  variant="minimal"
                                  size="sm"
                                  className="mr-2 !text-[10px] !px-1"
                                >
                                  {expense?.kind?.id === "flat"
                                    ? `x${form.values.trades?.length || 1}`
                                    : expense?.kind?.short}
                                </Tag>
                              )

                              return (
                                <>
                                  <tr
                                    key={
                                      expense.expense_parameter?.id ||
                                      `exp_${idx}`
                                    }
                                    // className="flex flex-col md:table-row w-full border-b md:border-0 pb-3 md:pb-0 gap-1 md:gap-0"
                                  >
                                    <td className="block w-full md:w-fit md:table-cell">
                                      <span className="md:hidden text-xs text-gray-500">
                                        Expense Parameter
                                      </span>
                                      <FormQuerySelector
                                        filterOption={(opt: any) => {
                                          if (
                                            isStatusGenerateSale &&
                                            !hasInvoice
                                          ) {
                                            return opt.data.codename ===
                                              Codenames.PackagingExpense
                                              ? true
                                              : false
                                          } else return true
                                        }}
                                        className="w-full"
                                        isOptionDisabled={(opt: any) => {
                                          return form.values.expenses?.find(
                                            exp =>
                                              Number(
                                                exp.expense_parameter?.id
                                              ) === Number(opt.id)
                                          )
                                            ? true
                                            : false
                                        }}
                                        menuPosition="fixed"
                                        disabled={isExpenseDisabled}
                                        dataHook={useExpenseParametersQuery}
                                        {...inputProps(
                                          `expenses.${idx}.expense_parameter`
                                        )}
                                      />
                                    </td>
                                    <td className="block w-full md:w-fit md:table-cell">
                                      <span className="md:hidden text-xs text-gray-500">
                                        Expense Type
                                      </span>
                                      <FormSelector
                                        className="w-full"
                                        // options={EXPENSE_KINDS.filter(kind => kind.id !== "flat")}
                                        options={EXPENSE_KINDS}
                                        disabled={isExpenseDisabled}
                                        menuPosition="fixed"
                                        {...inputProps(`expenses.${idx}.kind`)}
                                      />
                                    </td>
                                    {CHARGE_FIELDS.map(({ key, label }) => (
                                      <td
                                        key={key}
                                        className="block w-full md:w-fit md:table-cell"
                                      >
                                        <span className="md:hidden text-xs text-gray-500">
                                          {label}
                                        </span>
                                        <FormInput
                                          type="number"
                                          className="text-right"
                                          rightElement={rightElement}
                                          {...inputProps(
                                            `expenses.${idx}.${key}.value`
                                          )}
                                          disabled={isExpenseDisabled}
                                        />
                                      </td>
                                    ))}
                                    <td className="text-right block w-full md:w-fit md:table-cell">
                                      <span className="md:hidden text-xs text-gray-500 mr-2">
                                        Amount
                                      </span>

                                      {inr(expense.amount)}
                                    </td>
                                    <td className="block w-full md:w-fit md:table-cell">
                                      <button
                                        disabled={isExpenseDisabled}
                                        className={classNames(
                                          "w-10 flex justify-center",
                                          isExpenseDisabled
                                            ? "text-gray-600"
                                            : "text-red-500"
                                        )}
                                        onClick={() =>
                                          onDeleteExpense?.(idx, expense)
                                        }
                                      >
                                        <Trash
                                          height={24}
                                          width={24}
                                          className="!mr-0"
                                        />
                                      </button>
                                    </td>
                                  </tr>
                                </>
                              )
                            })}
                          </tbody>
                          <tfoot className="block w-full md:table-footer-group mt-3 md:mt-0">
                            <tr className="flex justify-between w-full md:table-row border-t border-gray-300 border-dashed pt-2 md:pt-0">
                              <td colSpan={2} className="block md:table-cell">
                                Total Expense
                                <span className="italic text-gray-500 text-sm">
                                  {" "}
                                  (Excl. Tax)
                                </span>
                              </td>
                              <td className="hidden md:table-cell"></td>
                              <td className="hidden md:table-cell"></td>
                              <td className="hidden md:table-cell"></td>
                              <td className="text-right font-500 block md:table-cell">
                                {inr(form.values.expense_amount || 0)}
                              </td>
                              <td className="hidden md:table-cell"></td>
                            </tr>
                          </tfoot>
                        </table>
                        {showOverallExpenseGst && (
                          <div
                            className={classNames(
                              "grid gap-3 mt-3",
                              isReceipt
                                ? "grid-cols-1"
                                : "grid-cols-1 md:grid-cols-2"
                            )}
                          >
                            <GstBreakdownCard
                              label="Supplier"
                              autoCalculated={
                                expenseGstManualOnly
                                  ? false
                                  : expenseSupplierGstAutoCalculated
                              }
                              autoToggleDisabled={expenseGstManualOnly}
                              onAutoChange={val => {
                                setExpenseGstField(
                                  "expense_supplier_gst_auto_calculated",
                                  val
                                )
                                form.setFieldValue(
                                  "_gstExpenseFromBackend",
                                  false
                                )
                              }}
                              isDisabled={isDisabled}
                              isSameState={supplierSameState}
                              totalGst={totalSupplierGst}
                              igst={expenseSupplierIgst}
                              sgst={expenseSupplierSgst}
                              cgst={expenseSupplierCgst}
                              igstPct={expenseSupplierIgstPercentage}
                              sgstPct={expenseSupplierSgstPercentage}
                              cgstPct={expenseSupplierCgstPercentage}
                              igstField="expense_supplier_igst_percentage"
                              sgstField="expense_supplier_sgst_percentage"
                              cgstField="expense_supplier_cgst_percentage"
                              onPctChange={(field, pct) =>
                                setExpenseGstField(field, pct)
                              }
                              attachments={expenseSellerGstAttachments}
                              onAddAttachment={newOnes =>
                                setExpenseGstField(
                                  "expense_seller_gst_attachments",
                                  [...expenseSellerGstAttachments, ...newOnes]
                                )
                              }
                              onRemoveAttachment={
                                onRemoveExpenseSellerGstAttachment
                              }
                            />
                            {!isReceipt && (
                              <GstBreakdownCard
                                label="Buyer"
                                autoCalculated={
                                  expenseGstManualOnly
                                    ? false
                                    : expenseBuyerGstAutoCalculated
                                }
                                autoToggleDisabled={expenseGstManualOnly}
                                onAutoChange={val =>
                                  setExpenseGstField(
                                    "expense_buyer_gst_auto_calculated",
                                    val
                                  )
                                }
                                isDisabled={isDisabled}
                                isSameState={buyerSameState}
                                totalGst={totalBuyerGst}
                                igst={expenseBuyerIgst}
                                sgst={expenseBuyerSgst}
                                cgst={expenseBuyerCgst}
                                igstPct={expenseBuyerIgstPercentage}
                                sgstPct={expenseBuyerSgstPercentage}
                                cgstPct={expenseBuyerCgstPercentage}
                                igstField="expense_buyer_igst_percentage"
                                sgstField="expense_buyer_sgst_percentage"
                                cgstField="expense_buyer_cgst_percentage"
                                onPctChange={(field, pct) =>
                                  setExpenseGstField(field, pct)
                                }
                                attachments={expenseBuyerGstAttachments}
                                onAddAttachment={newOnes =>
                                  setExpenseGstField(
                                    "expense_buyer_gst_attachments",
                                    [...expenseBuyerGstAttachments, ...newOnes]
                                  )
                                }
                                onRemoveAttachment={
                                  onRemoveExpenseBuyerGstAttachment
                                }
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <NoDataFound
                      className="min-h-[11rem]"
                      title="There are no expenses added yet"
                    >
                      <Button
                        onClick={() => form.insertListItem("expenses", EXPENSE)}
                        leftIcon={<Plus />}
                        size="sm"
                        disabled={
                          isStatusGenerateSale &&
                          hasInvoice &&
                          form.values.expenses?.find(
                            exp =>
                              exp?.expense_parameter?.codename ===
                              Codenames.PackagingExpense
                          ) !== undefined
                        }
                      >
                        Expense
                      </Button>
                    </NoDataFound>
                  )}
                </>
              ),
            },
            {
              id: "advancePayment",
              header: <h6 className="text-base font-500">Advance Payment</h6>,
              disabled: advancePaymentDisabled,
              panel: (
                <>
                  {form.values?.advance_payment?.length ? (
                    <div className="-mx-3 md:mx-0">
                      <div className="px-3 md:px-0">
                        <table className="expenses-table w-full block md:table">
                          <thead className="bg-green-100 text-sm rounded hidden md:table-header-group">
                            <tr>
                              <th className="p-1 w-[40%]">Advance Parameter</th>
                              <th className="p-1 w-[40%]">Borne by</th>
                              <th className="p-1 w-[40%]">Amount</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody className="block md:table-row-group">
                            {form.values?.advance_payment?.map(
                              (advance_payment, idx) => {
                                const isAdvanceDisabled = isDisabled
                                const is_verified = advance_payment?.is_verified

                                return (
                                  <tr
                                    key={advance_payment?.id || `exp_${idx}`}
                                    className="flex flex-col md:table-row w-full border-b md:border-0 pb-3 md:pb-0 gap-0.5 md:gap-0"
                                  >
                                    <td className="block w-full md:w-fit md:table-cell">
                                      <span className="md:hidden text-xs text-gray-500">
                                        Advance Parameter
                                      </span>
                                      <FormQuerySelector
                                        dataHook={
                                          useAdancedPaymentParametersQuery
                                        }
                                        filterOption={op => {
                                          return (
                                            op?.label !== "Transport Advance"
                                          )
                                        }}
                                        menuPosition="fixed"
                                        {...inputProps(
                                          `advance_payment.${idx}.parameter`
                                        )}
                                        disabled={isAdvanceDisabled}
                                      />
                                    </td>
                                    <td className="block w-full md:w-fit md:table-cell">
                                      <span className="md:hidden text-xs text-gray-500">
                                        Borne by
                                      </span>
                                      <FormSelector
                                        options={ADVANCE_KINDS}
                                        disabled={isAdvanceDisabled}
                                        menuPosition="fixed"
                                        getOptionValue={opt => opt.id}
                                        getOptionLabel={opt => opt.name}
                                        {...inputProps(
                                          `advance_payment.${idx}.bourn_by`
                                        )}
                                      />
                                    </td>
                                    <td className="block w-full md:w-fit md:table-cell">
                                      <span className="md:hidden text-xs text-gray-500">
                                        Amount
                                      </span>
                                      <FormInput
                                        disabled={isAdvanceDisabled}
                                        type="number"
                                        {...inputProps(
                                          `advance_payment.${idx}.amount`
                                        )}
                                        className="text-right"
                                      />
                                    </td>
                                    <td className="block w-full md:w-fit md:table-cell">
                                      {isAdvanceDisabled ||
                                      advance_payment?.id ? (
                                        is_verified === null ||
                                        is_verified === undefined ? (
                                          <Tooltip content="Pending">
                                            <Warning
                                              size={24}
                                              weight="fill"
                                              className="!mr-0 text-blue"
                                            />
                                          </Tooltip>
                                        ) : !is_verified ? (
                                          <Tooltip content="Rejected">
                                            <XCircle
                                              size={24}
                                              weight="fill"
                                              className="!mr-0 text-red"
                                            />
                                          </Tooltip>
                                        ) : (
                                          <Tooltip content="Verified">
                                            <CheckCircle
                                              size={24}
                                              weight="fill"
                                              className="!mr-0 text-green"
                                            />
                                          </Tooltip>
                                        )
                                      ) : (
                                        <button
                                          className={classNames(
                                            "w-10 flex ",
                                            isAdvanceDisabled ||
                                              advance_payment?.id
                                              ? "text-gray-600"
                                              : "text-red-500"
                                          )}
                                          disabled={
                                            isAdvanceDisabled ||
                                            advance_payment?.id
                                          }
                                          onClick={() =>
                                            onDeleteAdvancePayment?.(
                                              idx,
                                              advance_payment
                                            )
                                          }
                                        >
                                          <Trash
                                            height={24}
                                            width={24}
                                            className="!mr-0"
                                          />
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                )
                              }
                            )}
                          </tbody>
                          <tfoot className="block w-full md:table-footer-group mt-3 md:mt-0">
                            <tr className="flex justify-between w-full md:table-row border-t border-gray-300 border-dashed pt-2 md:pt-0">
                              <td colSpan={2} className="block md:table-cell">
                                Total Advance
                              </td>
                              <td className="text-right font-500 block md:table-cell">
                                {inr(form.values.advance_amount || 0)}
                              </td>
                              <td className="hidden md:table-cell"></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <NoDataFound
                      className="min-h-[11rem]"
                      title="There are no Advance Payment added yet"
                    >
                      <Button
                        onClick={() =>
                          form.insertListItem(
                            "advance_payment",
                            ADVANCED_PAYMENT
                          )
                        }
                        leftIcon={<Plus />}
                        size="sm"
                        disabled={isDisabled || advancePaymentDisabled}
                      >
                        Advance
                      </Button>
                    </NoDataFound>
                  )}
                </>
              ),
            },
            {
              id: "creditNote",
              panelClassName: "hidden",
              headerClassName(tab) {
                return "hidden"
              },
              header: (
                <h6 className="text-base font-500">Supplier Credit Note</h6>
              ),
              headerIcon: <Check size={20} />,
              disabled: creditNoteDisabled,
              panel: (
                <>
                  <div className="-mx-3 md:mx-0">
                    <div className="px-3 md:px-0">
                      <table className="expenses-table w-full block md:table">
                        <thead className="bg-green-100 text-sm rounded hidden md:table-header-group">
                          <tr>
                            <th className="p-1">Remark</th>
                            <th className="p-1 w-1/6">Amount</th>
                            <th className="p-1 w-3"></th>
                          </tr>
                        </thead>
                        <tbody className="block md:table-row-group">
                          {
                            <tr
                              key={1}
                              className="flex flex-col md:table-row w-full border-b md:border-0 pb-3 md:pb-0 gap-0.5 md:gap-0"
                            >
                              <td className="block w-full md:table-cell">
                                <span className="md:hidden text-xs text-gray-500">
                                  Remark
                                </span>
                                <FormInput
                                  placeholder="Remark"
                                  disabled={isDisabled}
                                  {...inputProps(`credit_notes.0.remarks`)}
                                />
                              </td>
                              <td className="block w-full md:table-cell">
                                <span className="md:hidden text-xs text-gray-500">
                                  Amount
                                </span>
                                <FormInput
                                  className="text-right"
                                  type="number"
                                  disabled={isDisabled}
                                  {...inputProps(`credit_notes.0.amount`)}
                                />
                              </td>
                              {form?.values?.credit_notes?.[0]?.id && (
                                <td className="block w-full md:table-cell">
                                  {isVerified === null ||
                                  isVerified === undefined ? (
                                    <Tooltip content="Pending">
                                      <Warning
                                        size={24}
                                        weight="fill"
                                        className="!mr-0 text-blue"
                                      />
                                    </Tooltip>
                                  ) : !isVerified ? (
                                    <Tooltip content="Rejected">
                                      <XCircle
                                        size={24}
                                        weight="fill"
                                        className="!mr-0 text-red"
                                      />
                                    </Tooltip>
                                  ) : (
                                    <Tooltip content="Verified">
                                      <CheckCircle
                                        size={24}
                                        weight="fill"
                                        className="!mr-0 text-green"
                                      />
                                    </Tooltip>
                                  )}
                                </td>
                              )}
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ),
            },
          ]}
          onChange={(_, idx) =>
            setActiveTab(() => {
              return idx === 1
                ? "advancePayment"
                : idx === 2
                ? "creditNote"
                : "tradeExpenses"
            })
          }
        >
          {activeTab === "tradeExpenses" ? (
            <Flex className="flex flex-col md:flex-row gap-2 md:ml-auto items-center mt-3">
              {!isDisabled && onExpenseTemplateChange && (
                <Selector
                  value={expenseTemplate}
                  options={expenseConfigList?.expense_config_versions?.map(
                    item => ({ id: item?.version_id, name: item?.name })
                  )}
                  className="font-400 w-full md:min-w-[15rem] md:w-auto"
                  onChange={async (item: any) => {
                    try {
                      const { data: expenseConfig } = await client
                        .query<
                          ExpenseConfigsQuery,
                          ExpenseConfigsQueryVariables
                        >(EXPENSE_CONFIGS, {
                          version: item?.id,
                          department: `${sharedDeptId}`,
                          kind: form.values.kind,
                          mode: form.values.mode,
                        })
                        .toPromise()
                      if (expenseConfig?.expense_configs?.length) {
                        onExpenseTemplateChange(expenseConfig)
                      }
                    } catch {
                    } finally {
                      setExpenseTemplate(item)
                    }
                  }}
                />
              )}
              <Button
                className="w-full md:w-auto"
                disabled={
                  isStatusGenerateSale && hasInvoice
                    ? true
                    : isStatusGenerateSale &&
                      !hasInvoice &&
                      form.values.expenses?.find(
                        exp =>
                          exp?.expense_parameter?.codename ===
                          Codenames.PackagingExpense
                      ) !== undefined
                }
                onClick={() => form.insertListItem("expenses", { ...EXPENSE })}
                leftIcon={<Plus />}
                size="sm"
              >
                Expense
              </Button>
            </Flex>
          ) : activeTab === "advancePayment" ? (
            <Flex className="flex md:ml-auto">
              <Button
                onClick={() =>
                  form.insertListItem("advance_payment", ADVANCED_PAYMENT)
                }
                leftIcon={<Plus />}
                size="sm"
                disabled={isDisabled || advancePaymentDisabled}
              >
                Advance
              </Button>
            </Flex>
          ) : activeTab === "creditNote" ? null : null}
        </Tabs>
      </FormCard>
    </>
  )
}

export default TradeExpenses
